import { FC, useState } from 'react';
import { Button, Col, Row } from 'antd';
import { capitalCase } from 'change-case';
import { TbChevronDown } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import AnimatedSiderCollection from '@/shared/components/layouts/siders/AnimatedSiderCollection';
import SiderIconLabel from '@/shared/components/layouts/siders/SiderIconLabel';
import SiderTooltip from '@/shared/components/tooltips/SiderTooltip';
import { WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';

import ResourceFavoriteItem from './components/01-ResourceFavoriteItem';

interface FavoritesCollapsibleSectionProps {
    collapsed?: boolean;
    data: Array<{ id?: string }>;
    forceExpanded?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
    section: FavoriteSection;
}

type FavoriteSection = 'dashboards' | 'folders' | 'investments' | 'lifecycles' | 'lists' | 'memos' | 'pipelines';

const FavoritesCollapsibleSection: FC<FavoritesCollapsibleSectionProps> = ({
    collapsed,
    data,
    forceExpanded,
    onCollapsedChange,
    section,
}): JSX.Element => {
    const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(false);
    const storedCollapsed = collapsed ?? uncontrolledCollapsed;
    const effectiveCollapsed = forceExpanded ? false : storedCollapsed;
    const setSectionCollapsed = (nextCollapsed: boolean) => {
        if (onCollapsedChange) {
            onCollapsedChange(nextCollapsed);

            return;
        }

        setUncontrolledCollapsed(nextCollapsed);
    };

    const { token } = useAntDesignTheme();

    return (
        <div data-favorite-resource-group={section}>
            <Row style={{ marginTop: '0.25rem' }} key={section}>
                <Col flex='auto'>
                    <SiderTooltip
                        title={
                            <>
                                {effectiveCollapsed ? 'Expand' : 'Collapse'} {section && capitalCase(section)} Favorites
                            </>
                        }
                    >
                        <Button
                            block
                            type='text'
                            size='small'
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                marginBottom: effectiveCollapsed ? 0 : '0.25rem',
                            }}
                            onClick={() => {
                                setSectionCollapsed(!storedCollapsed);
                            }}
                        >
                            <span
                                data-favorite-resource-group-header={section}
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    minWidth: 0,
                                    width: '100%',
                                }}
                            >
                                <small
                                    style={{
                                        alignItems: 'center',
                                        color: token.colorTextTertiary,
                                        display: 'inline-flex',
                                        lineHeight: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    <SiderIconLabel
                                        gap='0.25rem'
                                        icon={<WorkspaceResourceGlyph resource={section} />}
                                        label={section && capitalCase(section)}
                                        style={{ lineHeight: 1 }}
                                    />
                                </small>
                                <span
                                    aria-hidden='true'
                                    style={{
                                        alignItems: 'center',
                                        display: 'inline-flex',
                                        flex: '0 0 auto',
                                        justifyContent: 'center',
                                        lineHeight: 0,
                                        marginLeft: '0.5rem',
                                    }}
                                >
                                    <TbChevronDown
                                        style={{
                                            color: token.colorTextTertiary,
                                            transform: effectiveCollapsed ? 'rotate(-90deg)' : undefined,
                                            transition: 'transform 250ms ease',
                                        }}
                                    />
                                </span>
                            </span>
                        </Button>
                    </SiderTooltip>
                </Col>
            </Row>
            <AnimatedSiderCollection collapsed={effectiveCollapsed || data?.length === 0}>
                {data?.map((r) => (
                    <ResourceFavoriteItem key={r?.id} result={r} section={section} />
                ))}
            </AnimatedSiderCollection>
        </div>
    );
};

export default FavoritesCollapsibleSection;
